import { api } from '@/api';
import { DEFAULT_CURSOR_PAGE_LIMIT, type CursorPageParams } from '@/api/cursorPagination';
import {
  VISIBLE_USER_LEVELS,
  type DeviceUserLevelKeysResponse,
  type UserLevelKeyHistoryResponse,
  type VisibleUserLevel,
  type VisibleUserLevelKey,
} from '../types';

// SECURITY: both endpoints return plaintext PINs. Never log request or response bodies here
// and never write the results anywhere but the query cache of the open view.

// fetchDeviceUserLevelKeys loads the current keys and keeps only the visible levels 1-3.
// Superadmin only.
export async function fetchDeviceUserLevelKeys(deviceId: string): Promise<VisibleUserLevelKey[]> {
  const response = await api.get<DeviceUserLevelKeysResponse>(`/v1/devices/${deviceId}/user-level-keys`);
  return response.keys.filter((key): key is VisibleUserLevelKey => isVisibleUserLevel(key.userLevel));
}

// fetchDeviceUserLevelKeyHistory loads one page of past keys for one user level. The backend
// does not range-check keyIdx, so only the levels the UI shows may be requested.
export async function fetchDeviceUserLevelKeyHistory(
  deviceId: string,
  userLevel: VisibleUserLevel,
  { limit = DEFAULT_CURSOR_PAGE_LIMIT, cursor }: CursorPageParams = {}
): Promise<UserLevelKeyHistoryResponse> {
  return api.get<UserLevelKeyHistoryResponse>(
    `/v1/devices/${deviceId}/user-level-keys/${userLevel}/history`,
    { params: { limit, cursor } }
  );
}

export function isVisibleUserLevel(userLevel: number): userLevel is VisibleUserLevel {
  return (VISIBLE_USER_LEVELS as readonly number[]).includes(userLevel);
}
